import React, { useState, useMemo, useCallback } from 'react';
import { Modal, Input, Button, Tabs, message, List, Tag, DatePicker, Tooltip } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import RequestGeneralInfoTab from './RequestGeneralInfoTab.jsx';
import ListView from '../Vacancies/ListView.jsx';
import { updateMovementStatus, fetchVacancyRequests, fetchVacancyRequestByIdForUpdate, updateVacancyRequest, fetchVacanciesByVacancyRequest } from '../../redux/vacancyRequestsSlice';
import { createVacancy, fetchVacancies } from '../../redux/vacanciesSlice';
import CreateModal from '../Vacancies/CreateModal.jsx';
import { columnLabels, STATUSES } from '../../constants/vacancyRequestColumnLabels';
import StatusTag from '../StatusTag.jsx';
import {
  canRevokeVacancyRequest,
  canCommentOnVacancy,
  canCreateVacancyFromRequest
} from '../../utils/vacancyRequestPermissions';

const { TextArea } = Input;
const { TabPane } = Tabs;

const visibleColumns = [
  'id',
  'job_title',
  'organisation_id',
  'department_id',
  'assignee_id',
  'planned_close_date',
  'status',
  'actions'
];

const CustomComment = ({ item, users }) => {
  const user = users?.find(u => u.id === item.created_by);
  const authorName = user ? `${user.first_name} ${user.last_name}` : 'Неизвестный пользователь';

  return (
    <div style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
      <div style={{ fontWeight: 'bold' }}>{authorName}</div>
      <div style={{ margin: '4px 0' }}>{item.comment}</div>
      <div style={{ fontSize: 12, color: '#999' }}>
        {new Date(item.created_at).toLocaleString()}
      </div>
    </div>
  );
};

const ViewModal = ({
  visible,
  onCancel,
  request,
  vacancies,
  organisations,
  users,
  onCommentSubmit,
  currentUserId
}) => {

  const dispatch = useDispatch();
  const { orgId, role } = useSelector((state) => state.auth);
  const storedRole = typeof window !== 'undefined' ? localStorage.getItem('user_role') : null;
  const effectiveRole = role || storedRole;
  const { showAllRequests } = useSelector((state) => state.vacancyRequests);
  const [comment, setComment] = useState('');
  const [reminder, setReminder] = useState(null);
  const [reminderText, setReminderText] = useState('');
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [createModalInitialValues, setCreateModalInitialValues] = useState(null);
  const [rejectComment, setRejectComment] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [pendingRejectMovementId, setPendingRejectMovementId] = useState(null);
  const [withdrawComment, setWithdrawComment] = useState('');
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const { history, approvalRoute, isLoading, vacancies: requestVacancies } = useSelector(state => state.vacancyRequests);

  const shouldShowApprovalButtons = useCallback(() => {
    if (request?.is_template || request?.status === 'template') {
      return false;
    }

    // Не показывать кнопки согласования для отозванных заявок
    if (request?.status === 'withdrawn') {
      return false;
    }

    if (!approvalRoute || !approvalRoute.length || !currentUserId) {
      return false;
    }

    const currentUserRoute = approvalRoute.find(route => String(route.to_user) === String(currentUserId));

    if (!currentUserRoute || currentUserRoute.status !== 'new') {
      return false;
    }

    const currentUserIndex = approvalRoute.findIndex(route => String(route.to_user) === String(currentUserId));

    if (currentUserIndex === 0) {
      return true;
    }

    const previousRoute = approvalRoute[currentUserIndex - 1];
    return previousRoute && previousRoute.status === 'approve';
  }, [approvalRoute, currentUserId, request]);

  const canWithdrawRequest = useCallback(() => {
    if (request?.is_template ||
      request?.status === 'template' ||
      request?.status === 'draft' ||
      request?.status === 'withdrawn' ||
      request?.status === 'rejected' ||
      request?.status === 'closed' ||
      request?.status === 'archived') {
      return false;
    }

    const allowedStatuses = ['pending', 'new', 'in_progress', 'approved'];
    if (!allowedStatuses.includes(request?.status)) {
      return false;
    }

    if (!currentUserId) return false;

    // Используем централизованную проверку прав
    return canRevokeVacancyRequest(effectiveRole, currentUserId, {
      authorId: request?.author,
      approverHrId: request?.approver_hr_id
    });
  }, [request, currentUserId, effectiveRole]);

  const relatedVacancies = useMemo(
    () => (requestVacancies || []).filter(v => v.vacancy_request_id === request?.id),
    [requestVacancies, request?.id]
  );

  const handleOpenCreateModal = useCallback(() => {
    if (!request) return;
    // Проверяем права на создание вакансии из заявки
    if (!canCreateVacancyFromRequest(effectiveRole)) {
      message.error('У вас недостаточно прав для создания вакансии из заявки');
      return;
    }
    setCreateModalInitialValues(request);
    setCreateModalVisible(true);
  }, [request, effectiveRole]);

  const handleCloseCreateModal = useCallback(() => {
    setCreateModalVisible(false);
  }, []);

  // Data is now fetched by the Details page; keep only UI logic here

  const handleUpdateMovementStatusClick = useCallback(async (id, status, comment = '') => {
    if (!request) return;
    try {
      await dispatch(updateMovementStatus({
        movementId: id,
        status,
        requestId: request.id,
        organisationId: orgId,
        comment
      })).unwrap();

      await dispatch(fetchVacancyRequestByIdForUpdate({ id: request.id, organisationId: orgId }));

      const currentUserIndex = approvalRoute.findIndex(route => String(route.to_user) === String(currentUserId));
      const isLastUser = currentUserIndex === approvalRoute.length - 1;

      if (isLastUser && status === 'approve') {
        if (showAllRequests) {
          await dispatch(fetchVacancyRequests({ skip: 0, limit: 20, org_id: "all" })).unwrap();
        } else {
          await dispatch(fetchVacancyRequests({ skip: 0, limit: 20, org_id: orgId })).unwrap();
        }
      }

      message.success(`Заявка ${status === 'approve' ? 'согласована' : status === 'revision' ? 'на доработку' : 'закрыта'}`);
    } catch {
      message.error('Ошибка при обновлении статуса');
    }
  }, [dispatch, request, orgId, approvalRoute, currentUserId, showAllRequests]);

  const handleRejectClick = useCallback((movementId) => {
    setPendingRejectMovementId(movementId);
    setShowRejectModal(true);
  }, []);

  const handleRejectConfirm = useCallback(async () => {
    if (!rejectComment.trim()) {
      message.warning('При отзыве заявки комментарий с указанием причин является обязательным');
      return;
    }

    try {
      await handleUpdateMovementStatusClick(pendingRejectMovementId, 'rejected', rejectComment);
      setShowRejectModal(false);
      setRejectComment('');
      setPendingRejectMovementId(null);
      setTimeout(() => {
        onCancel();
      }, 100);
    } catch {
      message.error('Ошибка при отклонении заявки');
    }
  }, [rejectComment, pendingRejectMovementId, handleUpdateMovementStatusClick, onCancel]);

  const handleWithdrawClick = useCallback(() => {
    setShowWithdrawModal(true);
  }, []);

  const handleWithdrawConfirm = useCallback(async () => {
    if (!withdrawComment.trim()) {
      message.warning('При отзыве заявки комментарий с указанием причин является обязательным');
      return;
    }

    try {

      await dispatch(updateVacancyRequest({ id: request.id, organisationId: orgId, vacancyRequest: { status: 'withdrawn', comment: withdrawComment } })).unwrap();
      message.success('Заявка успешно отозвана');
      setShowWithdrawModal(false);
      setWithdrawComment('');

      await dispatch(fetchVacancyRequestByIdForUpdate({ id: request.id, organisationId: orgId }));

      if (showAllRequests) {
        await dispatch(fetchVacancyRequests({ skip: 0, limit: 20, org_id: "all" })).unwrap();
      } else {
        await dispatch(fetchVacancyRequests({ skip: 0, limit: 20, org_id: orgId })).unwrap();
      }

      setTimeout(() => {
        onCancel();
      }, 100);
    } catch (error) {
      console.error('Ошибка при отзыве заявки:', error);
      message.error('Ошибка при отзыве заявки');
    }
  }, [withdrawComment, request, dispatch, orgId, showAllRequests, onCancel]);

  const renderApprovalRoute = useCallback(() => {
    if (!approvalRoute.length) return <p>Маршрут согласования не найден</p>;

    const firstProcessedMovement = approvalRoute?.find(movement => movement.status !== 'new');

    return (
      <List
        dataSource={approvalRoute}
        renderItem={item => {
          const fromUser = users?.find(u => u.id === item.from_user);
          const toUser = users?.find(u => u.id === item.to_user);

          let statusText = '';
          let statusColor = '#666';

          switch (item.status) {
            case 'approve': statusText = 'Согласовано'; statusColor = '#52c41a'; break;
            case 'revision': statusText = 'На доработку'; statusColor = '#faad14'; break;
            case 'closed': statusText = 'Закрыто'; statusColor = '#f5222d'; break;
            case 'rejected': statusText = 'Отклонено'; statusColor = '#f5222d'; break;
            case 'new':
              statusText = firstProcessedMovement && item.id > firstProcessedMovement.id
                ? 'Ожидает результата' : 'На согласовании';
              statusColor = '#1890ff';
              break;
            default: statusText = item.status;
          }

          const showButtons = (() => {
            if (request?.is_template || request?.status === 'template' || request?.status === 'withdrawn') {
              return false;
            }

            if (String(item.to_user) !== String(currentUserId) || item.status !== 'new') {
              return false;
            }

            const currentIndex = approvalRoute.findIndex(route => route.id === item.id);

            if (currentIndex === 0) {
              return true;
            }

            const previousRoute = approvalRoute[currentIndex - 1];
            return previousRoute && previousRoute.status === 'approve';
          })();

          if (item.status === 'approve' && String(item.to_user) === String(currentUserId)) {
            statusText = 'Вы согласовали';
          }

          if (showButtons) {
            statusText = '';
          }

          return (
            <List.Item>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', width: '100%' }}>
                <div style={{ flex: 1 }}>
                  <div>
                    {fromUser ? `${fromUser.first_name} ${fromUser.last_name}` : 'Начало маршрута'} →
                    {toUser ? `${toUser.first_name} ${toUser.last_name}` : 'Неизвестный пользователь'}
                  </div>
                </div>
                <div style={{ marginLeft: 'auto', fontSize: '12px', color: statusColor, minWidth: '120px', textAlign: 'right' }}>
                  {statusText}
                </div>
                {showButtons && (
                  <div style={{ display: 'flex', gap: '8px', marginLeft: 16 }}>
                    <Tooltip title="Согласовать и передать далее по маршруту">
                      <Button size="small" type="primary" onClick={() => {
                        handleUpdateMovementStatusClick(item.id, 'approve')
                        onCancel();
                      }}>Согласовать</Button>
                    </Tooltip>
                    <Tooltip title="Отклонить заявку с комментарием">
                      <Button size="small" danger onClick={() => {
                        handleRejectClick(item.id)
                      }}>Отклонить</Button>
                    </Tooltip>
                  </div>
                )}
              </div>
            </List.Item>
          );
        }}
      />
    );
  }, [approvalRoute, users, currentUserId, handleUpdateMovementStatusClick, handleRejectClick, onCancel, request]);

  const handleCommentSubmit = useCallback(async () => {
    if (!comment.trim()) {
      message.warning('Введите комментарий перед отправкой');
      return;
    }

    // Проверяем права на оставление комментариев
    if (!canCommentOnVacancy(effectiveRole)) {
      message.error('У вас недостаточно прав для оставления комментариев');
      return;
    }

    try {
      if (onCommentSubmit) {
        await onCommentSubmit(request.id, comment);
      }
      message.success('Комментарий отправлен');
      setComment('');
      // history refresh handled by parent Details page after onCommentSubmit
    } catch {
      message.error('Ошибка при отправке комментария');
    }
  }, [comment, request, onCommentSubmit, effectiveRole]);

  const handleReminderSubmit = useCallback(() => {
    if (!reminder) {
      message.warning('Выберите дату и время для напоминания');
      return;
    }
    if (!reminderText.trim()) {
      message.warning('Введите текст напоминания');
      return;
    }
    setReminder(null);
    setReminderText('');
  }, [reminder, reminderText]);

  const handleCreateVacancy = useCallback(async (data) => {
    const newData = { ...data, vacancy_request_id: request.id };
    try {
      await dispatch(createVacancy({ organisationId: orgId, vacancy: newData })).unwrap();
      await dispatch(fetchVacancies({ skip: 0, limit: 20, org_id: "all" })).unwrap();
      await dispatch(fetchVacanciesByVacancyRequest({ id: request.id }));
      handleCloseCreateModal();
    } catch (error) {
      message.error('Ошибка при создании вакансии');
    }
  }, [request, handleCloseCreateModal, dispatch, orgId]);

  const getFieldDisplayName = useCallback((fieldName) => {
    return columnLabels[fieldName] || fieldName;
  }, []);

  const comments = useMemo(() => {
    if (!Array.isArray(history?.comments)) return [];
    return history?.comments?.filter(item => item.comment);
  }, [history]);

  const historyItems = useMemo(() => {
    if (!history || typeof history !== 'object' || !request) return [];

    const items = [];

    Object.entries(history).forEach(([fieldName, fieldHistory]) => {
      if (fieldName === 'comments' || !Array.isArray(fieldHistory)) return;

      const sortedHistory = [...fieldHistory].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

      sortedHistory.forEach((item, index) => {
        const originalFieldName = fieldName;
        const displayFieldName = getFieldDisplayName(fieldName);

        if (item.previous_value === false || item.previous_value === 'false') {
          return;
        }

        const formatValue = (val) => {
          if (fieldName === 'assignee_id' && val) {
            const user = users?.find(u => u.id === val || u.id === parseInt(val));
            return user ? `${user.first_name} ${user.last_name}` : `ID: ${val}`;
          }

          if (typeof val === 'object' && val !== null) {
            if (val.language && val.level) {
              return `${val.language.trim()} - ${val.level}`;
            }
            if (Array.isArray(val) && val.length > 0 && val[0].language && val[0].level) {
              return val.map(item => `${item.language.trim()} - ${item.level}`).join(', ');
            }
            return JSON.stringify(val);
          }
          if (typeof val === 'string' && val.startsWith('[') && val.includes('language')) {
            try {
              const parsed = JSON.parse(val);
              if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].language && parsed[0].level) {
                return parsed.map(item => `${item.language.trim()} - ${item.level}`).join(', ');
              }
            } catch (e) {
              const languageMatch = val.match(/language':\s*'([^']+)',\s*'level':\s*'([^']+)'/g);
              if (languageMatch) {
                const languages = languageMatch.map(match => {
                  const langMatch = match.match(/language':\s*'([^']+)',\s*'level':\s*'([^']+)'/);
                  if (langMatch) {
                    return `${langMatch[1].trim()} - ${langMatch[2]}`;
                  }
                  return null;
                }).filter(Boolean);
                if (languages.length > 0) {
                  return languages.join(', ');
                }
              }
            }
          }
          return val;
        };

        let currentValue;
        if (index === sortedHistory.length - 1) {
          currentValue = request[fieldName];
        } else {
          currentValue = sortedHistory[index + 1].previous_value;
        }

        items.push({
          ...item,
          field_name: displayFieldName,
          original_field_name: originalFieldName,
          previous_value: formatValue(item.previous_value === 'None' ? null : item.previous_value),
          current_value: formatValue(currentValue),
          is_initial_setup: item.previous_value === 'None'
        });
      });
    });

    return items.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [history, getFieldDisplayName, request, users]);


  const renderHistoryItem = useCallback((item) => {
    const user = users?.find(u => u.id === item.created_by);
    const authorName = user ? `${user.first_name} ${user.last_name}` : 'Неизвестный пользователь';

    const renderValue = (value, fieldName) => {

      if (fieldName === 'Исполнитель' && value) {
        const user = users?.find(u => u.id === value || u.id === parseInt(value));
        return user ? `${user.first_name} ${user.last_name}` : `${value}`;
      }

      if (value === 'true' || value === 'True' || value === true) {
        return <Tag color="green">Да</Tag>;
      }
      if (value === 'false' || value === 'False' || value === false) {
        return <Tag color="red">Нет</Tag>;
      }

      if (typeof value === 'string' && value.startsWith('[') && value.includes('language')) {
        try {
          const parsed = JSON.parse(value);
          if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].language && parsed[0].level) {
            return parsed.map(item => `${item.language.trim()} - ${item.level}`).join(', ');
          }
        } catch (e) {
          const languageMatch = value.match(/language':\s*'([^']+)',\s*'level':\s*'([^']+)'/g);
          if (languageMatch) {
            const languages = languageMatch.map(match => {
              const langMatch = match.match(/language':\s*'([^']+)',\s*'level':\s*'([^']+)'/);
              if (langMatch) {
                return `${langMatch[1].trim()} - ${langMatch[2]}`;
              }
              return null;
            }).filter(Boolean);
            if (languages.length > 0) {
              return languages.join(', ');
            }
          }
        }
      }

      if (typeof value === 'object' && value !== null) {
        if (value.language && value.level) {
          return `${value.language.trim()} - ${value.level}`;
        }
        if (Array.isArray(value) && value.length > 0 && value[0].language && value[0].level) {
          return value.map(item => `${item.language.trim()} - ${item.level}`).join(', ');
        }
        return JSON.stringify(value);
      }

      if (fieldName === columnLabels.status && STATUSES[value]) {
        return <StatusTag status={value} statuses={STATUSES} />;
      }

      return value || 'Не указано';
    };

    return (
      <div style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
        <div>
          <strong>{authorName}</strong> изменил поле: <strong>{item.field_name}</strong>
        </div>
        <div style={{ marginTop: 4 }}>
          {item.is_initial_setup ? (
            <span style={{ color: '#999' }}>Установлено значение: </span>
          ) : (
            <>
              <span style={{ color: '#999' }}>Было: </span>
              {renderValue(item.previous_value, item.field_name)}
              <span style={{ margin: '0 8px' }}>→</span>
            </>
          )}
          <span style={{ fontWeight: 'bold' }}>{renderValue(item.current_value, item.field_name)}</span>
        </div>
        {item.comment && (
          <div style={{ marginTop: 4, fontStyle: 'italic', color: '#666' }}>
            Комментарий: {item.comment}
          </div>
        )}
        <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
          {new Date(item.created_at).toLocaleString()}
        </div>
      </div>
    );
  }, [users]);

  return (
    <Modal title="Просмотр заявки" visible={visible} onCancel={onCancel} footer={null} width={1000}>
      {request && (
        <>
          <Tabs defaultActiveKey="1" tabBarExtraContent={
            <div style={{ display: 'flex', gap: '8px' }}>
              {shouldShowApprovalButtons() && (
                <>
                  <Tooltip title="Согласовать заявку и передать на следующий этап согласования">
                    <Button type="primary" size="small" onClick={async () => {
                      try {
                        const currentUserRoute = approvalRoute.find(route => String(route.to_user) === String(currentUserId));
                        if (currentUserRoute) {
                          await dispatch(updateMovementStatus({
                            movementId: currentUserRoute.id,
                            status: 'approve',
                            requestId: request.id,
                            organisationId: orgId
                          })).unwrap();

                          await dispatch(fetchVacancyRequestByIdForUpdate({ id: request.id, organisationId: orgId }));

                          const currentUserIndex = approvalRoute.findIndex(route => String(route.to_user) === String(currentUserId));
                          const isLastUser = currentUserIndex === approvalRoute.length - 1;

                          if (isLastUser) {
                            if (showAllRequests) {
                              await dispatch(fetchVacancyRequests({ skip: 0, limit: 20, org_id: "all" })).unwrap();
                            } else {
                              await dispatch(fetchVacancyRequests({ skip: 0, limit: 20, org_id: orgId })).unwrap();
                            }
                          }
                          onCancel();
                        }
                      } catch {
                        message.error('Ошибка при согласовании');
                      }
                    }}>Согласовать</Button>
                  </Tooltip>
                  <Tooltip title="Отклонить заявку с указанием причины отклонения">
                    <Button size="small" danger onClick={() => {
                      const currentUserRoute = approvalRoute.find(route => String(route.to_user) === String(currentUserId));
                      if (currentUserRoute) {
                        handleRejectClick(currentUserRoute.id);
                      }
                    }}>Отклонить</Button>
                  </Tooltip>
                </>
              )}
              {canWithdrawRequest() && (
                <Tooltip title="Отозвать заявку из процесса согласования и закрыть её">
                  <Button size="small" danger onClick={handleWithdrawClick}>
                    Отозвать
                  </Button>
                </Tooltip>
              )}
            </div>
          }>
            <TabPane tab="Заявка" key="1">
              <RequestGeneralInfoTab request={request} vacancies={vacancies} organisations={organisations} users={users} />
            </TabPane>
            <TabPane tab="Вакансия" key="2">
              {console.log("request", request)}
              {canCreateVacancyFromRequest(effectiveRole) && (
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                  <Tooltip title="Создать вакансию на основе данных из заявки">
                    <Button type="primary" onClick={handleOpenCreateModal}>Создать вакансию</Button>
                  </Tooltip>
                </div>
              )}
              {relatedVacancies.length > 0 ? (
                <ListView
                  vacancies={relatedVacancies}
                  organisations={organisations}
                  users={users}
                  visibleColumns={visibleColumns}
                  isModalMode={true}
                />
              ) : <p>Вакансия не найдена.</p>}
              <CreateModal isOpen={createModalVisible} onClose={handleCloseCreateModal} onSubmit={handleCreateVacancy} organisations={organisations} users={users} initialValues={createModalInitialValues} />
            </TabPane>
            {!request?.is_template && (
              <TabPane tab="Маршрут согласования" key="3">
                {renderApprovalRoute()}
              </TabPane>
            )}
            {!request?.is_template && (
              <TabPane tab="История" key="4" loading={isLoading}>
                {historyItems.length > 0 ? (
                  <List dataSource={historyItems} renderItem={renderHistoryItem} />
                ) : (
                  <p>История изменений отсутствует</p>
                )}
              </TabPane>
            )}
            <TabPane tab="Комментарии" key="5" loading={isLoading}>
              {Array.isArray(comments) && comments.length > 0 ? (
                <List className="comment-list" header={`${comments.length} комментария(ев)`} itemLayout="horizontal" dataSource={comments} renderItem={item => <CustomComment item={item} users={users} />} />
              ) : <p>Нет комментариев</p>}
            </TabPane>
          </Tabs>
        </>
      )}
      {canCommentOnVacancy(effectiveRole) && (
        <div style={{ marginTop: 24 }}>
          <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 8 }}>Ваш комментарий</label>
          <TextArea rows={4} value={comment} onChange={e => setComment(e.target.value)} placeholder="Оставьте комментарий..." />
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
            <Tooltip title="Добавить комментарий к заявке">
              <Button type="primary" onClick={handleCommentSubmit}>Отправить комментарий</Button>
            </Tooltip>
          </div>
        </div>
      )}

      {canCommentOnVacancy(effectiveRole) && (
        <div style={{ marginTop: 32 }}>
          <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 8 }}>
            Напоминание
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <DatePicker
              showTime
              value={reminder}
              onChange={setReminder}
              placeholder="Выберите дату и время"
              style={{ width: '100%' }}
              inputReadOnly={true}
            />
            <TextArea
              rows={3}
              value={reminderText}
              onChange={e => setReminderText(e.target.value)}
              placeholder="О чем напомнить..."
              style={{ width: '100%' }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Tooltip title="Установить напоминание о заявке">
                <Button
                  type="default"
                  onClick={handleReminderSubmit}
                >
                  Напомнить
                </Button>
              </Tooltip>
            </div>
          </div>
        </div>
      )}

      <Modal
        title="Отклонение заявки"
        visible={showRejectModal}
        onCancel={() => {
          setShowRejectModal(false);
          setRejectComment('');
          setPendingRejectMovementId(null);
        }}
        onOk={handleRejectConfirm}
        okText="Отклонить"
        cancelText="Отмена"
        okButtonProps={{
          danger: true,
          disabled: !rejectComment.trim()
        }}
      >
        <div>
          <p style={{ marginBottom: 16, color: '#666' }}>
            При отклонении заявки комментарий с указанием причин является обязательным
          </p>
          <TextArea
            required
            rows={4}
            value={rejectComment}
            onChange={e => setRejectComment(e.target.value)}
            placeholder="Укажите причину отклонения заявки..."
            style={{ marginBottom: 16 }}
            status={rejectComment.trim() === '' ? 'error' : ''}
          />
          {rejectComment.trim() === '' && (
            <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '-12px', marginBottom: '16px' }}>
              Комментарий обязателен для заполнения
            </div>
          )}
        </div>
      </Modal>

      <Modal
        title="Отзыв заявки"
        visible={showWithdrawModal}
        onCancel={() => {
          setShowWithdrawModal(false);
          setWithdrawComment('');
        }}
        onOk={handleWithdrawConfirm}
        okText="Отозвать"
        cancelText="Отмена"
        okButtonProps={{
          danger: true,
          disabled: !withdrawComment.trim()
        }}
      >
        <div>
          <p style={{ marginBottom: 16, color: '#666' }}>
            При отзыве заявки комментарий с указанием причин является обязательным.
            Заявка будет закрыта, а связанные вакансии, кандидаты и задачи будут уведомлены о необходимости закрытия.
          </p>
          <TextArea
            required
            rows={4}
            value={withdrawComment}
            onChange={e => setWithdrawComment(e.target.value)}
            placeholder="Укажите причину отзыва заявки..."
            style={{ marginBottom: 16 }}
            status={withdrawComment.trim() === '' ? 'error' : ''}
          />
          {withdrawComment.trim() === '' && (
            <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '-12px', marginBottom: '16px' }}>
              Комментарий обязателен для заполнения
            </div>
          )}
        </div>
      </Modal>
    </Modal>
  );
};

export default React.memo(ViewModal);